import React from 'react';
import { Box, Typography } from '@mui/material';
import { BarList, Sheet, SpecGrid } from '../../../components/ui';
import { sbg } from '../../../theme/sbgTokens';
import { formatCount, formatNumber, formatPercent } from '../../../utils/format';

export interface OrgSummaryStats {
  total: number;
  managers: number;
  hybrid: number;
  roots: number;
  teams: [string, number][];
}

/** Headcount at a glance; picking a team finds its people in the chart. */
const OrgSummarySheet: React.FC<{
  open: boolean;
  onClose: () => void;
  stats: OrgSummaryStats;
  freshness: string | null;
  onPickTeam: (team: string) => void;
  /** False when the viewer gets the directory, which carries no WFH setup to count. */
  showHybrid?: boolean;
}> = ({ open, onClose, stats, freshness, onPickTeam, showHybrid = true }) => (
  <Sheet
    open={open}
    onClose={onClose}
    title="Org summary"
    subtitle={['Active employees', freshness].filter(Boolean).join(' · ')}
  >
    <SpecGrid
      minColumnWidth={140}
      items={[
        { label: 'People', value: formatNumber(stats.total), caption: 'Active employees' },
        {
          label: 'Managers',
          value: formatNumber(stats.managers),
          caption: stats.total > 0 ? `${formatPercent((stats.managers / stats.total) * 100)} of people` : undefined,
        },
        ...(showHybrid ? [{ label: 'Hybrid eligible', value: formatNumber(stats.hybrid), caption: 'Approved to work from home' }] : []),
        { label: 'Top level', value: formatNumber(stats.roots), caption: 'No manager on file' },
      ]}
    />

    <Box sx={{ display: 'grid', gap: 1.5, mt: 2 }}>
      <Box>
        <Typography variant="h4" component="h3" sx={{ color: sbg.text.primary }}>
          People by team
        </Typography>
        <Typography variant="body2" component="p" sx={{ color: sbg.text.secondary }}>
          {formatCount(stats.teams.length, 'team')}. Select one to find its people in the chart.
        </Typography>
      </Box>
      {stats.teams.length > 0 ? (
        <BarList
          items={stats.teams.map(([name, count]) => ({
            key: name,
            label: name,
            value: count,
            display: formatNumber(count),
            tooltip: `Find ${formatCount(count, 'person', 'people')} in ${name}`,
            onClick: () => onPickTeam(name),
          }))}
        />
      ) : (
        <Typography variant="body1" sx={{ color: sbg.text.secondary }}>
          No teams yet. Teams appear once people are assigned to one.
        </Typography>
      )}
    </Box>
  </Sheet>
);

export default OrgSummarySheet;
